"use client";
import SectionTitle from "@/components/shared/sectionTitle";
import { Separator } from "@/components/ui/separator";
import { ClientServiceDetailsResponse } from "@/types/service";
import Image from "next/image";
import { cn, getFilePath } from "@/lib/utils/utils";
import Banner from "@/components/shared/banner";
import useLocalizer from "@/hooks/useLocalizer";
import { Activity } from "react";
import Flex from "@/components/core/flex";
import GalleryContainer from "@/components/shared/galleryContainer";
import ContactInfoBox from "@/components/shared/contactInfoBox";

interface Props {
  className?: string;
  data: ClientServiceDetailsResponse;
}

const ServiceDetailsContent = ({ data, className }: Props) => {
  const clientServiceDetails = data;
  const { isRtl, t } = useLocalizer();
  return (
    <section className={cn("relative", className)}>
      <div className="relative h-80">
        <Banner
          title={
            isRtl
              ? (clientServiceDetails?.service?.arTitle ?? "")
              : (clientServiceDetails?.service?.enTitle ?? "")
          }
          image={
            clientServiceDetails?.service?.image
              ? getFilePath(clientServiceDetails?.service?.image?.key)
              : undefined
          }
          breadcrumbItems={[
            {
              name: isRtl
                ? (clientServiceDetails?.arTitle ?? "")
                : (clientServiceDetails?.enTitle ?? ""),
              href: "/services",
            },
            {
              name: isRtl
                ? (clientServiceDetails?.service?.arTitle ?? "")
                : (clientServiceDetails?.service?.enTitle ?? ""),
            },
          ]}
        />
      </div>
      <div className="w-full h-full mb-20 mt-10 px-5 lg:px-0 ">
        <div className="container mx-auto grid lg:grid-cols-3 w-full gap-8">
          <div className="lg:col-span-2">
            <Flex direction="col" spacing={{ all: 8 }}>
              <Activity
                mode={
                  clientServiceDetails?.service?.image ? "visible" : "hidden"
                }
              >
                <div className="relative h-[445px] w-full">
                  <Image
                    src={getFilePath(
                      clientServiceDetails?.service?.image?.key!,
                    )}
                    alt={
                      isRtl
                        ? (clientServiceDetails?.service?.arTitle ?? "")
                        : (clientServiceDetails?.service?.enTitle ?? "")
                    }
                    fill
                    priority
                    placeholder="empty"
                    className="object-cover"
                  />
                </div>
              </Activity>
              <Flex direction="col" spacing={{ all: 4 }}>
                <SectionTitle
                  title={
                    isRtl
                      ? (clientServiceDetails?.service?.arTitle ?? "")
                      : (clientServiceDetails?.service?.enTitle ?? "")
                  }
                  subTitleTag="h3"
                  subTitle={
                    isRtl
                      ? (clientServiceDetails?.service?.arSubTitle ?? "")
                      : (clientServiceDetails?.service?.enSubTitle ?? "")
                  }
                  description={
                    isRtl
                      ? (clientServiceDetails?.service?.arDescription ?? "")
                      : (clientServiceDetails?.service?.enDescription ?? "")
                  }
                />
              </Flex>
              <Separator className="my-5" />
              <Activity
                mode={
                  clientServiceDetails?.gallery &&
                  clientServiceDetails?.gallery?.length > 0
                    ? "visible"
                    : "hidden"
                }
              >
                <Flex direction="col" spacing={{ all: 4 }}>
                  <SectionTitle
                    titleTag="h3"
                    title={t("titles.serviceImages")}
                    description={t("paragraphs.serviceImagesDescription")}
                  />

                  <GalleryContainer
                    images={
                      clientServiceDetails?.gallery?.map((image) => ({
                        path: image.image?.key ?? "",
                        alt: image.image?.alt ?? "",
                      })) ?? []
                    }
                  />
                </Flex>
              </Activity>
            </Flex>
          </div>
          <ContactInfoBox
            image={
              clientServiceDetails?.service?.image
                ? getFilePath(clientServiceDetails?.service?.image?.key)
                : undefined
            }
            data={{
              client: clientServiceDetails?.client,
              socialMedia: clientServiceDetails?.socialMedia,
              periods: clientServiceDetails?.periods,
              covers: clientServiceDetails?.covers,
            }}
          />
        </div>
      </div>
    </section>
  );
};

export default ServiceDetailsContent;
